import { useCallback, useMemo, useState } from "react";
import { Task } from "@common/types/task";
import {
    useFinalizeMigratedDataMapping,
    useImportedAccounts,
} from "@api/accountingIntegration.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { AccountClassMismatchDescription } from "./AccountClassMismatchDescription";
import { getAccountClassMismatches } from "./getAccountClassMismatches";

interface Props {
    selectedTask: Task;
}

const TaskMapMigratedDataAction = ({ selectedTask }: Props) => {
    const entityId = selectedTask.entityIds?.[0];

    const { data: importedAccountsData, isLoading } = useImportedAccounts(
        selectedTask.workspaceId,
        entityId,
        { requiresFinancialAccountMapping: true, orderBy: "institution" },
    );
    const importedAccounts = importedAccountsData?.accounts;

    const financialAccounts = useFinancialAccounts({ entityId });

    const { toast } = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const onDataMappingFinalized = useCallback(() => {
        toast("Migrated data mapping confirmed");
    }, [toast]);

    const { mutate: finalize, isPending } = useFinalizeMigratedDataMapping(
        entityId,
        onDataMappingFinalized,
    );

    const [isWarningOpen, setIsWarningOpen] = useState(false);

    const areAllAccountsMapped = useMemo(
        () =>
            !!importedAccounts &&
            importedAccounts.length > 0 &&
            importedAccounts.every(
                (a) => a.mappedFinancialAccountId != null || a.isClosed,
            ),
        [importedAccounts],
    );

    const accountClassMismatches = useMemo(
        () =>
            getAccountClassMismatches({
                importedAccounts: importedAccounts ?? [],
                financialAccounts,
            }),
        [importedAccounts, financialAccounts],
    );

    const closeWarning = useCallback(() => {
        setIsWarningOpen(false);
    }, []);

    const handleClick = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        if (accountClassMismatches.length > 0) {
            setIsWarningOpen(true);
            return;
        }
        finalize();
    }, [finalize, accountClassMismatches, refuseIfViewOnly]);

    const handleConfirmWarning = useCallback(() => {
        closeWarning();
        finalize();
    }, [closeWarning, finalize]);

    const finishTooltip = useViewOnlyTooltipReason(
        areAllAccountsMapped ? "" : "Map all accounts before finishing",
    );

    if (!entityId || isLoading) {
        return null;
    }

    return (
        <>
            <ButtonWithLoader
                loading={isPending}
                variant="primary"
                size="md"
                disabled={!areAllAccountsMapped}
                tooltip={finishTooltip || undefined}
                onClick={handleClick}
            >
                Finish mapping
            </ButtonWithLoader>

            <ConfirmationModal
                show={isWarningOpen}
                onHide={closeWarning}
                onReject={closeWarning}
                onConfirm={handleConfirmWarning}
                title="Merging liability and asset accounts"
                question={
                    <>
                        {accountClassMismatches.map((mismatch) => (
                            <p key={mismatch.importedAccountId}>
                                <AccountClassMismatchDescription
                                    mismatch={mismatch}
                                />
                            </p>
                        ))}
                        <p>
                            Historical reports might change after this merge. Do
                            you want to proceed?
                        </p>
                    </>
                }
                yes="Finish mapping"
                no="Cancel"
            />
        </>
    );
};

export const MapMigratedDataCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3" sticky>
        <TaskMapMigratedDataAction selectedTask={selectedTask} />
    </CommandIsland>
);
